import React, { useCallback, useState } from "react";
import * as myConstList from "../Common/BaseUrl";
import Footer from "../Common/Footer";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import MainHeader from "../Common/MainHeader";
import Nav from "../Common/Nav";
import { useListingAddMutation } from "../../redux/userApi";

const baseUrl = myConstList.baseUrl;

// Validation utility (centralized)
const validateField = (name, value, options = {}, allValues = {}) => {
  const { min, max, allowSpace = true } = options;
  const stringValue = typeof value === "string" ? value : "";
  const trimmedValue = stringValue.trim();

  // Only applies for text-like fields
  const textFields = ["token name", "short name", "introduction"];

  if (textFields.includes(name) && !allowSpace) {
    // no leading/trailing spaces
    if (/^\s/.test(stringValue) || /\s$/.test(stringValue)) {
      return `Please enter a valid ${name} (no leading/trailing spaces)`;
    }
    // no consecutive spaces
    if (/\s{2,}/.test(stringValue)) {
      return `Please enter a valid ${name} (no consecutive spaces)`;
    }
  }

  switch (name) {
    case "token name":
      if (!trimmedValue) return "Please enter token name";
      if (!/^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/.test(trimmedValue)) {
        return "Token name can only contain letters, numbers and single spaces";
      }
      if (trimmedValue.length < min || trimmedValue.length > max) {
        return `Token name must be between ${min}-${max} characters`;
      }
      return "";

    case "short name":
      if (!trimmedValue) return "Please enter short name";
      if (!/^[a-zA-Z0-9]+$/.test(trimmedValue)) {
        return "Short name can only contain letters and numbers";
      }
      if (trimmedValue.length < min || trimmedValue.length > max) {
        return `Short name must be ${min}-${max} characters`;
      }
      return "";

    case "decimals":
      if (!trimmedValue) return "Please enter decimals";
      if (!/^\d+$/.test(trimmedValue)) {
        return "Decimals must be a whole number";
      }
      return "";

    case "issue price":
    case "max supply":
    case "circulating supply": {
      if (!trimmedValue) return `Please enter ${name}`;
      const regex = /^\d{1,8}(\.\d{1,8})?$/;
      if (!regex.test(trimmedValue)) {
        return `${name} must be like 12345678.12345678 (max 8 before and 8 after decimal)`;
      }

      // extra cross-field validations
      if (name === "max supply" && allValues["circulating supply"]) {
        if (Number(trimmedValue) < Number(allValues["circulating supply"])) {
          return "Max supply must be greater than circulating supply";
        }
      }
      if (name === "circulating supply" && allValues["max supply"]) {
        if (Number(trimmedValue) > Number(allValues["max supply"])) {
          return "Circulating supply must be less than max supply";
        }
      }

      return "";
    }

    case "issue date": {
      if (!trimmedValue) return "Please enter issue date";
      const today = new Date();
      today.setHours(0, 0, 0, 0); // normalize to midnight
      const enteredDate = new Date(trimmedValue);
      if (enteredDate < today) {
        return "Issue date cannot be before today";
      }
      return "";
    }

    case "explorer link":
    case "website": {
      if (!trimmedValue) return `Please enter ${name}`;
      try {
        const parsed = new URL(trimmedValue);
        if (!["http:", "https:"].includes(parsed.protocol)) {
          return `Please enter a valid ${name} (http/https and valid domain)`;
        }
        if (!parsed.hostname || !parsed.hostname.includes(".")) {
          return `Please enter a valid ${name} (http/https and valid domain)`;
        }
      } catch {
        return `Please enter a valid ${name} (http/https and valid domain)`;
      }
      return "";
    }

    case "logo":
      if (!value) return "Please select logo";
      if (value.size < 10240 || value.size > 2048000) {
        return "Logo size should be 10KB to 2MB";
      }
      if (!/\.(jpg|jpeg|png)$/i.test(value.name)) {
        return "Only jpg, jpeg, png allowed";
      }
      return "";

    case "document":
      if (!value) return "";
      if (value.size < 10240 || value.size > 5242880) {
        return "Document size should be 10KB to 5MB";
      }
      if (!/\.pdf$/i.test(value.name)) {
        return "Only pdf allowed";
      }
      return "";

    case "introduction":
      if (!trimmedValue) return "";
      if (trimmedValue.length < min || trimmedValue.length > max) {
        return `Introduction must be ${min}-${max} characters`;
      }
      return "";

    case "chain":
      if (!trimmedValue) return "Please enter chain";
      if (trimmedValue.length < min || trimmedValue.length > max) {
        return `Chain must be ${min}-${max} characters`;
      }
      return "";

    case "contract address":
      if (!trimmedValue) return "Please enter contract address";
      if (/\s/.test(trimmedValue)) {
        return "Contract address cannot contain spaces";
      }
      if (trimmedValue.length < min || trimmedValue.length > max) {
        return `Contract address must be ${min}-${max} characters`;
      }
      return "";

    default:
      return "";
  }
};


const ApplyListing = () => {
  const [formData, setFormData] = useState({
    name: "",
    shortName: "",
    logo: null,
    issuePrice: "",
    decimals: "",
    issueDate: "",
    maxSupply: "",
    circulatingSupply: "",
    explorer: "",
    website: "",
    chain: "",
    contractAddress: "",
    document: null,
    introduction: "",
  });

  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState("");
  const [btnDisable, setBtnDisable] = useState(false);

  const [addListing] = useListingAddMutation();

  // Prevent invalid chars in number input
  const handleValidation = useCallback((event) => {
    const disallowed = new Set([
      "e",
      "E",
      "-",
      "+",
      "!",
      "@",
      "#",
      "$",
      "%",
      "^",
      "&",
      "*",
      "(",
      ")",
    ]);
    if (disallowed.has(event.key)) event.preventDefault();
  }, []);

  // Handle input/file change
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    const file = files && files[0] ? files[0] : null;

    // ------------------------
    // Max date check for issueDate
    if (name === "issueDate" && value) {
      const maxDate = new Date("9999-01-01");
      const selectedDate = new Date(value);
      if (selectedDate > maxDate) {
        // setErrors((prev) => ({
        //   ...prev,
        //   [name]: "Please enter valid date",
        // }));
        // optionally reset the field or keep previous value
        return;
      } else {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    }
    // ------------------------

    const newValue = name === "shortName" ? value.toUpperCase() : file || value;

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // determine key for validation
    let fieldKey = name;
    if (name === "name") fieldKey = "token name";
    if (name === "shortName") fieldKey = "short name";
    if (name === "issuePrice") fieldKey = "issue price";
    if (name === "decimals") fieldKey = "decimals";
    if (name === "maxSupply") fieldKey = "max supply";
    if (name === "circulatingSupply") fieldKey = "circulating supply";
    if (name === "explorer") fieldKey = "explorer link";
    if (name === "website") fieldKey = "website";
    if (name === "chain") fieldKey = "chain";
    if (name === "contractAddress") fieldKey = "contract address";
    if (name === "document") fieldKey = "document";
    if (name === "logo") fieldKey = "logo";
    if (name === "introduction") fieldKey = "introduction";
    if (name === "issueDate") fieldKey = "issue date";

    const err = validateField(fieldKey, newValue, {
      min:
        name === "introduction"
          ? 10
          : name === "name"
          ? 2
          : name === "shortName"
          ? 1
          : name === "chain"
          ? 2
          : name === "contractAddress"
          ? 6
          : undefined,
      max:
        name === "introduction"
          ? 300
          : name === "name"
          ? 35
          : name === "shortName"
          ? 20
          : name === "chain"
          ? 50
          : name === "contractAddress"
          ? 120
          : undefined,
      allowSpace: !(name === "name" || name === "shortName"),
    });

    setErrors((prev) => ({ ...prev, [name]: err }));

    if (name === "logo" && !err && file) {
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const keyMap = {
    name: "token_name",
    shortName: "short_name",
    issuePrice: "issue_price",
    decimals: "decimals",
    maxSupply: "max_supply",
    circulatingSupply: "circulating_supply",
    issueDate: "issue_date",
    explorer: "explorer_link",
    website: "website_link",
    chain: "chain",
    contractAddress: "contract_address",
    introduction: "introduction",
    logo: "logo",
    document: "document",
  };

  // Submit handler
  const submit = async (e) => {
    e.preventDefault();
    let hasError = false;
    const newErrors = {};

    // validate all fields
    Object.entries(formData).forEach(([key, val]) => {
      let fieldKey = key;
      if (key === "name") fieldKey = "token name";
      if (key === "shortName") fieldKey = "short name";
      if (key === "issuePrice") fieldKey = "issue price";
      if (key === "decimals") fieldKey = "decimals";
      if (key === "maxSupply") fieldKey = "max supply";
      if (key === "circulatingSupply") fieldKey = "circulating supply";
      if (key === "explorer") fieldKey = "explorer link";
      if (key === "website") fieldKey = "website";
      if (key === "chain") fieldKey = "chain";
      if (key === "contractAddress") fieldKey = "contract address";
      if (key === "document") fieldKey = "document";
      if (key === "logo") fieldKey = "logo";
      if (key === "introduction") fieldKey = "introduction";
      if (key === "issueDate") fieldKey = "issue date";

      const err = validateField(fieldKey, val, {
      min:
        key === "introduction"
          ? 10
          : key === "name"
          ? 2
          : key === "shortName"
          ? 1
          : key === "chain"
          ? 2
          : key === "contractAddress"
          ? 6
          : undefined,
        max:
          key === "introduction"
            ? 300
            : key === "name"
          ? 35
          : key === "shortName"
          ? 20
          : key === "chain"
          ? 50
          : key === "contractAddress"
          ? 120
          : undefined,
        allowSpace: !(key === "name" || key === "shortName"),
      });
      if (err) {
        newErrors[key] = err;
        hasError = true;
      }
    });

    setErrors(newErrors);
    if (hasError) return;

    const uploadData = new FormData();
    Object.entries(formData).forEach(([key, val]) => {
      const apiKey = keyMap[key]; // find corresponding API key
      if (
        apiKey &&
        val !== null &&
        val !== undefined &&
        !(typeof val === "string" && val === "")
      ) {
        uploadData.append(apiKey, val);
      }
    });

    try {
      setBtnDisable(true);
      toast.dismiss();

      const res = await addListing(uploadData).unwrap();
      toast.success(res.message);

      setTimeout(() => {
        window.location.href = "/listing";
      }, 2000);
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.data?.message || error?.message || "Something went wrong"
      );

      if (error?.data?.message === "Failed to authenticate token.") {
        setTimeout(() => (window.location.href = "/login"), 2500);
      }
    } finally {
      setBtnDisable(false);
    }
  };

  return (
    <div>
      <Nav graphColor="" />
      <div className="trade-volume">
        <div className="container">
          <div className="section-heading text-center">
            <h2>Add Coin/Token</h2>
          </div>
        </div>
      </div>
      <div className="listing-main">
        <div className="container">
          <div className="listing-outer">
            <div className="listing-form">
              <form onSubmit={submit}>
                <div className="row">
                  {/* Name */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Coin/Token Name</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Enter Coin/Token Name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      <span style={{ color: "red" }}>{errors.name}</span>
                    </div>
                  </div>

                  {/* Short Name */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Short Name</label>
                      <input
                        type="text"
                        name="shortName"
                        placeholder="Enter Short Name"
                        className="form-control"
                        value={formData.shortName}
                        onChange={handleChange}
                      />
                      <span style={{ color: "red" }}>{errors.shortName}</span>
                    </div>
                  </div>

                  {/* Issue Price */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Issue Price</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          name="issuePrice"
                          placeholder="Issue Price"
                          className="form-control"
                          value={formData.issuePrice}
                          onChange={handleChange}
                          onKeyDown={handleValidation}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </div>
                      <span style={{ color: "red" }}>{errors.issuePrice}</span>
                    </div>
                  </div>

                  {/* Decimals */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Decimals</label>
                      <input
                        type="number"
                        name="decimals"
                        placeholder="Decimals"
                        className="form-control"
                        value={formData.decimals}
                        onChange={handleChange}
                        onKeyDown={handleValidation}
                        onWheel={(e) => e.currentTarget.blur()}
                        min="0"
                        step="1"
                      />
                      <span style={{ color: "red" }}>{errors.decimals}</span>
                    </div>
                  </div>

                  {/* Issue Date */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Issue Date</label>
                      <input
                        type="date"
                        name="issueDate"
                        className="form-control"
                        value={formData.issueDate}
                        onChange={handleChange}
                      />
                      <span style={{ color: "red" }}>{errors.issueDate}</span>
                    </div>
                  </div>

                  {/* Max Supply */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Max Supply</label>
                      <input
                        type="number"
                        name="maxSupply"
                        placeholder="Max Supply"
                        className="form-control"
                        value={formData.maxSupply}
                        onChange={handleChange}
                        onKeyDown={handleValidation}
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                      <span style={{ color: "red" }}>{errors.maxSupply}</span>
                    </div>
                  </div>

                  {/* Circulating Supply */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Circulating Supply</label>
                      <input
                        type="number"
                        name="circulatingSupply"
                        placeholder="Circulating Supply"
                        className="form-control"
                        value={formData.circulatingSupply}
                        onChange={handleChange}
                        onKeyDown={handleValidation}
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                      <span style={{ color: "red" }}>
                        {errors.circulatingSupply}
                      </span>
                    </div>
                  </div>

                  {/* Explorer Link */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Explorer Link</label>
                      <input
                        type="text"
                        name="explorer"
                        placeholder="Explorer Link"
                        className="form-control"
                        value={formData.explorer}
                        onChange={handleChange}
                      />
                      <span style={{ color: "red" }}>{errors.explorer}</span>
                    </div>
                  </div>

                  {/* Website */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Website Link</label>
                      <input
                        type="text"
                        name="website"
                        placeholder="Website Link"
                        className="form-control"
                        value={formData.website}
                        onChange={handleChange}
                      />
                      <span style={{ color: "red" }}>{errors.website}</span>
                    </div>
                  </div>

                  {/* Chain */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Chain</label>
                      <input
                        type="text"
                        name="chain"
                        placeholder="Chain"
                        className="form-control"
                        value={formData.chain}
                        onChange={handleChange}
                      />
                      <span style={{ color: "red" }}>{errors.chain}</span>
                    </div>
                  </div>

                  {/* Contract Address */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Contract Address</label>
                      <input
                        type="text"
                        name="contractAddress"
                        placeholder="Contract Address"
                        className="form-control"
                        value={formData.contractAddress}
                        onChange={handleChange}
                      />
                      <span style={{ color: "red" }}>
                        {errors.contractAddress}
                      </span>
                    </div>
                  </div>

                  {/* Logo */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>Logo</label>
                      <input
                        type="file"
                        name="logo"
                        className="form-control"
                        onChange={handleChange}
                        accept="image/*"
                      />
                      {logoPreview && (
                        <img
                          src={logoPreview}
                          alt="Preview"
                          className="img-fluid"
                        />
                      )}
                      <span style={{ color: "red" }}>{errors.logo}</span>
                    </div>
                  </div>

                  {/* Document */}
                  <div className="col-sm-6 col-12">
                    <div className="form-group">
                      <label>White PP Document (Optional)</label>
                      <input
                        type="file"
                        name="document"
                        className="form-control"
                        accept=".pdf"
                        onChange={handleChange}
                      />
                      <span style={{ color: "red" }}>{errors.document}</span>
                    </div>
                  </div>

                  {/* Introduction */}
                  <div className="col-12">
                    <div className="form-group">
                      <label>Introduction (Optional)</label>
                      <textarea
                        name="introduction"
                        placeholder="Introduction"
                        className="form-control"
                        value={formData.introduction}
                        onChange={handleChange}
                      ></textarea>
                      <span style={{ color: "red" }}>
                        {errors.introduction}
                      </span>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="col-12 text-right">
                    <div className="btn-group save-cancel">
                      <button
                        type="submit"
                        className="btn btn-success"
                        disabled={btnDisable}
                      >
                        Submit
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => (window.location.href = "/listing")}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ApplyListing;
